import { HoverPopover } from '@app/components/base/HoverPopover';
import { usePlaybackSeekInRange } from '@app/core/playback/hooks/usePlayback';
import { nlpIntegration } from '@mi-front/api-clients';
import { getQueryParam, toTimestamp } from '@mi-front/core';
import { useMemo } from 'react';
import { useActiveStation } from '../../../../../../../core/stations/hooks/useActiveStation';
import { STREAM_UI_CONFIG } from '../../../consts';
import { useStreamSegmentsRange } from '../../../hooks/segments/useStreamSegmentsQuery';
import { useSetCursor } from '../../../stores/stream-screen.store';
import type { EntitiesDisplayMode } from '../entities/TopicEntities';
import { getLiveTopicsDemoMappedToRange } from '../helpers/demo-data';
import { TRACK_REFETCH_INTERVAL_MS } from '../panel/consts';
import { LiveTopicPopupContent } from './popup/LiveTopicPopupContent';
import { LiveTopicsEmptyStrip } from './track-items/LiveTopicsEmptyStrip';
import { TopicSegmentPill } from './track-items/TopicSegmentPill';

const isLiveTopicsDebug = () => getQueryParam('live-topics-debug') === '1';

const getEntitiesDisplayMode = (): EntitiesDisplayMode =>
  getQueryParam('live-topics-entities') === 'long' ? 'long' : 'short';

const TRACK_HEIGHT = STREAM_UI_CONFIG.focusTracksHeight;

export type { EntitiesDisplayMode } from '../entities/TopicEntities';

/**
 * Live Topics track: renders above the waveform, below the thumbnail roll.
 * Shows pills (display title) per topic with hover popup (title + summary).
 * When the range has no topics, shows a hatched empty strip.
 */
export function LiveTopicsTrack() {
  const { slug } = useActiveStation();
  const range = useStreamSegmentsRange();
  const seekInRange = usePlaybackSeekInRange();
  const setCursor = useSetCursor();
  const fromDatetime = range.from > 0 ? new Date(range.from).toISOString() : '';
  const toDatetime = range.to > 0 ? new Date(range.to).toISOString() : '';
  const debugMode = isLiveTopicsDebug();
  const enabled = !!slug && range.from > 0 && range.to > 0;
  const apiEnabled = enabled && !debugMode;

  const { data, isLoading } = nlpIntegration.useQueryTopicsLiveTopics(
    slug,
    fromDatetime && toDatetime
      ? { from_datetime: fromDatetime, to_datetime: toDatetime }
      : undefined,
    {
      query: { enabled: apiEnabled, refetchInterval: TRACK_REFETCH_INTERVAL_MS },
    },
  );

  const demoTopics = useMemo(
    () => (debugMode && enabled ? getLiveTopicsDemoMappedToRange(range) : null),
    [debugMode, enabled, range.from, range.to],
  );

  const topics = debugMode && demoTopics ? demoTopics : (data ?? []);
  /** Chronological order so later topics paint above earlier ones when pills overlap. */
  const sortedTopics = useMemo(
    () => [...topics].sort((a, b) => toTimestamp(a.start_time) - toTimestamp(b.start_time)),
    [topics],
  );
  const hasTopics = sortedTopics.length > 0;
  const showEmptyState = enabled && !isLoading && !hasTopics;
  const entitiesDisplayMode = getEntitiesDisplayMode();

  if ((!debugMode && isLoading) || !enabled) {
    return null;
  }

  if (showEmptyState) {
    return <LiveTopicsEmptyStrip />;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: TRACK_HEIGHT,
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      <div style={{ pointerEvents: 'auto', height: '100%' }}>
        {sortedTopics.map((topic, index) => (
          <HoverPopover
            trigger={
              <TopicSegmentPill
                topic={topic}
                zIndex={index + 1}
                onClick={() => {
                  const ts = toTimestamp(topic.start_time);
                  setCursor(ts);
                  seekInRange(ts);
                }}
              />
            }
            key={topic.id}
            positionMode="cursor-x-element-y"
            cursorOffset={{ top: -16 }}
            delay={STREAM_UI_CONFIG.trackPopoverDelayMs}
            contentSx={{ p: 0, maxWidth: 'none' }}
            paperSx={{ minWidth: 320, maxWidth: 480 }}
          >
            <LiveTopicPopupContent topic={topic} entitiesDisplayMode={entitiesDisplayMode} />
          </HoverPopover>
        ))}
      </div>
    </div>
  );
}
